import * as _ from "lodash";

import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

var service = function (C) {
  var retailerEvents = C.RETAILER_ORDER_EVENTS;

  var sortEvents = function (events) {
    if (!events || !(events.length || 0) > 0) {
      return [];
    }
    var idx = _.findIndex(events, function (x) {
      return (x || {}).action == "order_submitted";
    });
    events.splice(0, 0, events.splice(idx, 1)[0]);
    var idxApprovalEmail = _.findIndex(events, function (x) {
      return (x || {}).action == "order_approval_email";
    });
    if (idxApprovalEmail > -1) {
      events.splice(0, 0, events.splice(idxApprovalEmail, 1)[0]);
    }

    var idxApproval = _.findIndex(events, function (x) {
      return (x || {}).action == "order_submitted_for_approval";
    });
    if (idxApproval > -1) {
      events.splice(0, 0, events.splice(idxApproval, 1)[0]);
    }

    if (events) {
      var isVermont = events.find(e => e.distributor_id == VT_802_SPIRITS_DISTRIBUTOR_ID);
      var acceptedEvent = event =>
        isVermont
          ? event.action == "status_updated" && event.event_data.to == "Confirmed"
          : event.action == "order_accepted";
      var acceptedIdx = _.findIndex(events, function (x) {
        return acceptedEvent(x);
      });
      if (acceptedIdx !== null) {
        events.push(events.splice(acceptedIdx, 1)[0]);
      }
    }
    return events;
  };

  var getEventActorName = function (event) {
    return event.actor_name;
  };

  var eventActorNames = function (events) {
    return _.compact(_.sortBy(_.uniq(_.map(events, getEventActorName))));
  };

  var combineEvents = function (events, actions, message) {
    var otherEvents = _.reject(events, function (e) {
      return _.includes(actions, e.action);
    });
    var relevant = _.filter(events, function (e) {
      return _.includes(actions, e.action);
    });
    var names = eventActorNames(relevant);

    if (relevant.length) {
      var newCombinedEvent = {
        action: actions[0],
        created_at: (relevant[0] || {}).created_at,
        event_data: { msg: message + _.join(names, ", ") },
      };
    } else {
      return events;
    }
    return _.concat(otherEvents, newCombinedEvent);
  };

  var combine = function (events) {
    var newOrders = combineEvents(
      events,
      ["new_order_email", "new_order_sms"],
      "New order sent to ",
    );
    var seen = combineEvents(newOrders, ["order_seen"], "Order seen by ");
    return _.sortBy(_.compact(seen), function (e) {
      return e.created_at;
    });
  };

  var generateStatusMessage = function (event, status) {
    const warehouseName = _.startCase(event.warehouse_name.toLowerCase());
    const statusMessages = {
      Seen: `Order ready for pickup at ${warehouseName}.`,
      Confirmed: `Order complete at ${warehouseName}.`,
      Rejected: `Order rejected at ${warehouseName}.`,
    };
    return statusMessages[status];
  };

  var sanitizeMsg = function (event) {
    if (!event || event.action === "new_concierge_order_email") {
      return;
    }
    if (event.action == "status_updated" && event.event_data && event.event_data.to) {
      event.event_data.msg = generateStatusMessage(event, event.event_data.to);
    }
    event.event_data.msg = _.split(event.event_data.msg, " (")[0];
  };

  var keepFirstEventOfAction = function (events, action, additionalFilter = null) {
    const firstEvent = _.find(events, function (x) {
      const matchesAction = x.action === action;
      return additionalFilter ? matchesAction && additionalFilter(x) : matchesAction;
    });

    if (firstEvent) {
      _.remove(events, function (x) {
        const isNotFirstEvent = x.order_event_id !== firstEvent.order_event_id;
        const matchesAction = x.action === action;
        const matchesAdditionalFilter = additionalFilter ? additionalFilter(x) : true;
        return isNotFirstEvent && matchesAction && matchesAdditionalFilter;
      });
    }
    return firstEvent;
  };

  var removeEventsByAction = function (events, action, additionalFilter = null) {
    _.remove(events, function (x) {
      const matchesAction = x.action === action;
      const matchesAdditionalFilter = additionalFilter ? additionalFilter(x) : true;
      return matchesAction && matchesAdditionalFilter;
    });
  };

  var filterVermontEvents = function (events, state, totalItems) {
    // Keep only first "ready for pickup" event
    keepFirstEventOfAction(events, "status_updated", x => x.event_data.to === "Seen");

    // Handle confirmed events
    const confirmedEvents = _.filter(
      events,
      x => x.action === "status_updated" && x.event_data.to === "Confirmed",
    );
    const firstConfirmed = confirmedEvents[0];

    if (confirmedEvents.length && totalItems) {
      if (state === "Complete" && confirmedEvents.length < totalItems) {
        const partialAcceptanceEvent = {
          action: "order_partially_accepted",
          event_data: {
            num_accepted: confirmedEvents.length,
            num_total: totalItems,
            msg: `${confirmedEvents.length} of ${totalItems} items complete at ${_.startCase(
              firstConfirmed.warehouse_name.toLowerCase(),
            )}.`,
          },
          created_at: firstConfirmed.created_at,
          warehouse_name: firstConfirmed.warehouse_name,
        };
        events.push(partialAcceptanceEvent);
      }
    }

    if (firstConfirmed) {
      keepFirstEventOfAction(events, "status_updated", x => x.event_data.to === "Confirmed");
    }

    // Handle rejected events
    const rejectedEvents = _.filter(
      events,
      x => x.action === "status_updated" && x.event_data.to === "Rejected",
    );
    if (rejectedEvents.length === totalItems) {
      keepFirstEventOfAction(events, "status_updated", x => x.event_data.to === "Rejected");
    } else {
      removeEventsByAction(events, "status_updated", x => x.event_data.to === "Rejected");
    }

    // Remove "Sent" status events
    removeEventsByAction(events, "status_updated", x => x.event_data.to === "Sent");
  };

  var filterNonVermontEvents = function (events) {
    keepFirstEventOfAction(events, "order_accepted");
    keepFirstEventOfAction(events, "order_rejected");
    removeEventsByAction(events, "status_updated");
  };

  var applyAppSpecificFiltering = function (events, app, state) {
    removeEventsByAction(events, "quantity_updated", x => x.actor_type !== "SalesRep");

    if (app === "distributor") {
      keepFirstEventOfAction(events, "order_seen");
    }

    if (app === "retailer" && state !== "accepted" && state !== "Complete") {
      const eventsToRemoveAfterAccept = [
        "product_removed_by_rep",
        "product_backordered",
        "quantity_updated",
        "out_of_stock",
      ];
      _.remove(events, x => eventsToRemoveAfterAccept.includes(x.action));
    }
  };

  var filterEvents = function (app, events, state = "", totalItems = null) {
    if (app === "retailer" || app === "distributor") {
      events = _.filter(events, event => _.includes(retailerEvents, event.action));
      const isVermont = events.find(e => e.distributor_id === VT_802_SPIRITS_DISTRIBUTOR_ID);

      if (isVermont) {
        filterVermontEvents(events, state, totalItems);
      } else {
        filterNonVermontEvents(events);
      }

      applyAppSpecificFiltering(events, app, state);

      _.each(events, sanitizeMsg);
    }

    events = combine(events);
    return sortEvents(events);
  };

  return {
    filterEvents: filterEvents,
  };
};

service.$inject = ["C"];

tiz.service("OrderEvent", service);
export default service;
